/**
 * La campana de notificaciones de la cabecera.
 *
 * QUE ES. Un "ponte al dia" pasivo: reacciones a tus mazos publicados, el meta
 * recien actualizado y -- para los admin -- las altas, mazos compartidos y
 * entradas del buzon. El servidor guarda HECHOS (tipo + datos); la frase se
 * redacta aqui, en el idioma del lector, para que cambiar de idioma traduzca
 * tambien lo ya recibido.
 *
 * COMO SE LEE. Abrir el panel marca todo como leido de una vez (es una campana,
 * no un gestor de tareas), pero lo que estaba sin leer conserva su punto dorado
 * mientras el panel siga abierto: primero se ve QUE es lo nuevo, y a la
 * siguiente visita ya no cuenta como tal.
 *
 * CUANDO SE CONSULTA. Al entrar, al volver a la pestaña y cada cinco minutos.
 * Sin sesion no existe: la campana es de cosas TUYAS.
 */

import { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n.jsx';
import { useAuth } from '../auth.jsx';
import { api } from '../api.js';
import { useVolverCierra } from '../movimiento.js';

const CADA_MS = 5 * 60 * 1000;

/** Glifo por tipo, como los sucesos del detalle de partida. */
const GLIFO = {
  voto: '♥',
  meta: '▲',
  nuevoUsuario: '＋',
  mazoCompartido: '⇪',
  buzon: '✉',
};

export default function Campana() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [abierta, setAbierta] = useState(false);
  const [items, setItems] = useState([]);
  const [sinLeer, setSinLeer] = useState(0);
  /** Ids que estaban sin leer al abrir: conservan su punto hasta cerrar. */
  const [nuevas, setNuevas] = useState(() => new Set());
  const rootRef = useRef(null);

  const cargar = () => {
    api('/api/notifications')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setItems(d.notificaciones ?? []);
        setSinLeer(d.sinLeer ?? 0);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!user) {
      setItems([]);
      setSinLeer(0);
      return undefined;
    }
    cargar();
    const alVolver = () => document.visibilityState === 'visible' && cargar();
    document.addEventListener('visibilitychange', alVolver);
    const id = setInterval(cargar, CADA_MS);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      clearInterval(id);
    };
  }, [user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Pulsar fuera o Escape cierran; "atras" en el movil tambien (misma capa
  // que cualquier otro panel).
  useEffect(() => {
    if (!abierta) return undefined;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setAbierta(false);
    };
    const onKey = (e) => e.key === 'Escape' && setAbierta(false);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [abierta]);
  useVolverCierra(abierta, () => setAbierta(false));

  if (!user) return null;

  const abrir = () => {
    const abriendo = !abierta;
    setAbierta(abriendo);
    if (!abriendo) return;
    if (sinLeer > 0) {
      setNuevas(new Set(items.filter((n) => !n.leida).map((n) => n.id)));
      setSinLeer(0);
      setItems((prev) => prev.map((n) => ({ ...n, leida: true })));
      /*
       * El refresco espera a que el marcado TERMINE. Refrescar en paralelo era
       * una carrera contra uno mismo: la lista volvia con el contador viejo
       * (la peticion salio antes del POST) y pisaba el cero recien puesto --
       * la campana se quedaba con el numero encendido tras abrirla.
       */
      api('/api/notifications/leidas', { method: 'POST' })
        .then(() => cargar())
        .catch(() => {});
    } else {
      setNuevas(new Set());
      cargar();
    }
  };

  /** La frase de cada hecho, en el idioma vigente. */
  const frase = (n) => {
    const d = n.datos ?? {};
    switch (n.tipo) {
      case 'voto': return t('notifVoto', { mazo: d.mazo ?? '' });
      case 'meta': return t('notifMeta', { n: d.torneos ?? 0 });
      case 'nuevoUsuario':
        return t('notifNuevoUsuario', { quien: d.nombre ? `${d.nombre} · ${d.correo}` : (d.correo ?? '') });
      case 'mazoCompartido':
        return t('notifMazoCompartido', { autor: d.autor ?? '', mazo: d.mazo ?? '' });
      case 'buzon':
        return d.tipo === 'bug' ? t('notifBuzonBug') : t('notifBuzonIdea');
      case 'suscripcion':
        return t('notifSuscripcion', { correo: d.correo ?? '', estado: d.estado ?? '' });
      case 'precio':
        return t('notifPrecio', {
          carta: d.carta ?? '',
          tienda: d.tienda ?? '',
          precio: Number(d.precio ?? 0).toLocaleString('es-CL'),
          referencia: Number(d.referencia ?? 0).toLocaleString('es-CL'),
        });
      default: return n.tipo;
    }
  };

  const haceCuanto = (iso) => {
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return '';
    const min = Math.floor(ms / 60000);
    if (min < 1) return t('notifAhora');
    if (min < 60) return t('notifHaceMin', { n: min });
    const h = Math.floor(min / 60);
    if (h < 24) return t('notifHaceHoras', { n: h });
    const dias = Math.floor(h / 24);
    if (dias < 30) return t('notifHaceDias', { n: dias });
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-US', {
      day: 'numeric', month: 'short',
    });
  };

  return (
    <div className="campana" ref={rootRef}>
      <button
        type="button"
        className={`campana__btn${abierta ? ' is-open' : ''}`}
        onClick={abrir}
        aria-haspopup="dialog"
        aria-expanded={abierta}
        aria-label={sinLeer > 0 ? t('notifBellUnread', { n: sinLeer }) : t('notifBell')}
        title={t('notifBell')}
      >
        <svg viewBox="0 0 16 17" aria-hidden="true">
          <path
            d="M8 1.5a4.6 4.6 0 0 0-4.6 4.6c0 3.4-1 4.6-1.9 5.5h13c-.9-.9-1.9-2.1-1.9-5.5A4.6 4.6 0 0 0 8 1.5z"
            fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
          />
          <path d="M6.3 14.2a1.8 1.8 0 0 0 3.4 0" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        {sinLeer > 0 && (
          <span className="campana__badge" aria-hidden="true">
            {sinLeer > 9 ? '9+' : sinLeer}
          </span>
        )}
      </button>

      <div
        className={`campana__panel${abierta ? ' is-open' : ''}`}
        role="dialog"
        aria-label={t('notifBell')}
        inert={!abierta}
      >
        <div className="campana__cabecera">
          <h3 className="campana__titulo">{t('notifTitle')}</h3>
          {/* Limpiar borra SOLO lo leido: lo que se abre ahora mismo (aun
              marcado como nuevo) tambien cuenta, porque ya se esta viendo. */}
          {items.length > 0 && (
            <button
              type="button"
              className="campana__limpiar"
              onClick={() => {
                setItems((prev) => prev.filter((n) => !n.leida));
                api('/api/notifications/leidas', { method: 'DELETE' }).catch(() => {});
              }}
            >
              {t('notifClear')}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="campana__vacio muted">{t('notifEmpty')}</p>
        ) : (
          <ul className="campana__lista">
            {items.map((n) => (
              <li key={n.id} className={`campana__item${nuevas.has(n.id) ? ' is-nueva' : ''}`}>
                <span className={`campana__glifo es-${n.tipo}`} aria-hidden="true">
                  {GLIFO[n.tipo] ?? '•'}
                </span>
                <span className="campana__texto">
                  {frase(n)}
                  <span className="campana__cuando muted">{haceCuanto(n.creadaEn)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
